import { useState, useEffect, useRef } from 'react'
import { Col, Row } from 'antd'
import { SaveFilled } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { DragDropContext, Droppable, Draggable, BeforeCapture } from '@hello-pangea/dnd'
import { NavLink } from 'react-router-dom'
import { SettingOutlined } from '@ant-design/icons'
import { getBox } from 'css-box-model'
import { retrieveGalleryIdFromDroppableId, retrieveImageIdFromDraggableId } from '../../../Helpers/GalleryHelper'
import { renderTranslation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { CLIENT_PAGES_PATH } from '../../../Constants/ClientRoutes'
import { Gallery, Image, addImage, removeImage, resetEdit } from '../../../Slices/PIM/ImagesSlices'
import { saveImageChanges } from '../../../Services/PIM/ImageService'
import { photoboxDroppableId } from '../../../Constants/Configuration'
import EditPhotoModal from '../../../Components/Generic/EditPhotoModal'
import DraggableImage from '../../../Components/Kiosk/DraggableImage'
import PimGallery from '../../../Components/Kiosk/PimGallery'
import UploadImageButton from './UploadImageButton'

function Images() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const images = useAppSelector(state => state.present.images.images)
  const fetchedImages = useAppSelector(state => state.present.images.fetchedImages)
  const galleries = useAppSelector(state => state.present.images.galleries)
  const isEdited = useAppSelector(state => state.present.images.isEdited)
  const workspaceImagesVisible = useAppSelector(state => state.present.configuration.workspaceImagesVisible)

  const [editId, setEditId] = useState(undefined)
  const clientSelectionRef = useRef({ x: 0, y: 0 })

  const saveChanges = e => {
    dispatch(saveImageChanges()).then(response => {
      if (response) {
        dispatch(resetEdit(undefined))
      }
    })
  }

  const imageSection = document.getElementById('multi-table-container')

  const setDraggableImageComponent = (
    containerClassName,
    className,
    draggableProvided,
    draggableSnapshot,
    imageId,
    imageUrl,
    enableMenu,
    usedCount?,
    setEditId?,
    type?,
  ) => {
    return (
      <DraggableImage
        containerClassName={containerClassName}
        draggableProps={draggableProvided.draggableProps}
        dragHandleProps={draggableProvided.dragHandleProps}
        draggableSnapshot={draggableSnapshot}
        ref={draggableProvided.innerRef}
        imageId={`${imageId}`}
        imageUrl={imageUrl}
        className={'responsive-img' + (className ? ' ' + className : '')}
        usedCount={usedCount}
        enableMenu={enableMenu}
        setEditId={setEditId}
        type={type}
      />
    )
  }

  const bindElementPositionOnCursor = (event: CustomEvent) => {
    if (event.detail.before.draggableId.indexOf('gallery') === -1) {
      const droppableImage: HTMLElement = document.querySelector(
        `[data-rfd-draggable-id='${event.detail.before.draggableId}']`,
      )

      const clientSelection = event.detail.clientSelection

      const box = getBox(droppableImage)

      //centered dragged element as much as possible to the cursor
      const targetWidth: number = 40 //force element to keep this width
      const halfWidth: number = targetWidth / 2
      const halfHeight: number = droppableImage.offsetHeight / 2
      const distanceToLeft: number = clientSelection.x - box.borderBox.left
      const distanceToTop: number = clientSelection.y - box.borderBox.top

      droppableImage.style.width = `${targetWidth}px`

      // Nothing left to do
      if (distanceToLeft < halfWidth) {
        return
      }

      //new left and top distance offset
      const proposedLeftOffset: number = distanceToLeft - halfWidth
      const proposedTopOffset: number = distanceToTop - halfHeight
      //raw right and top value
      const targetRight: number = box.borderBox.left + proposedLeftOffset + targetWidth
      const targetBottom: number = box.borderBox.top + proposedTopOffset + droppableImage.offsetHeight

      // how much past the right and bottom value
      const rightOverlap: number = Math.max(targetRight - box.borderBox.right, 0)
      const bottomOverlap: number = Math.max(targetBottom - box.borderBox.bottom, 0)

      //new right and bottom position
      const leftOffset: number = proposedLeftOffset - rightOverlap
      const topOffset: number = proposedLeftOffset - bottomOverlap

      droppableImage.style.position = 'relative'
      droppableImage.style.left = `${leftOffset}px`
      droppableImage.style.top = `${topOffset}px`
    }
  }

  useEffect(() => {
    window.addEventListener('onBeforeCapture', bindElementPositionOnCursor, { passive: true })

    return () => {
      window.removeEventListener('onBeforeCapture', bindElementPositionOnCursor)
    }
  }, [])

  const getMousePosition = (event: MouseEvent) => {
    const current = {
      x: event.clientX,
      y: event.clientY,
    }
    clientSelectionRef.current = current
  }

  useEffect(() => {
    window.addEventListener('mousemove', getMousePosition, { passive: true })

    return () => {
      window.removeEventListener('mousemove', getMousePosition)
    }
  })

  function onBeforeCapture(before: BeforeCapture) {
    window.dispatchEvent(
      new CustomEvent('onBeforeCapture', {
        detail: { before, clientSelection: clientSelectionRef.current },
      }),
    )
  }

  return workspaceImagesVisible ? (
    <Row className='pa-2'>
      <EditPhotoModal
        editId={editId}
        onModalCancel={() => {
          setEditId(undefined)
        }}
      />
      <DragDropContext
        onBeforeCapture={onBeforeCapture}
        onDragEnd={result => {
          const { source, destination, draggableId } = result
          if (!destination) {
            if (source.droppableId !== photoboxDroppableId) {
              dispatch(
                removeImage({
                  galleryId: parseInt(retrieveGalleryIdFromDroppableId(source.droppableId)),
                  imageId: parseInt(retrieveImageIdFromDraggableId(draggableId)),
                }),
              )
            }

            return
          }

          if (source.droppableId === destination.droppableId && destination.index === source.index) {
            return
          }

          const payload = {
            galleryId: parseInt(retrieveGalleryIdFromDroppableId(destination.droppableId)),
            imageId:
              source.droppableId === photoboxDroppableId
                ? parseInt(draggableId) //draggable id from preview box is always image id
                : parseInt(retrieveImageIdFromDraggableId(draggableId)),
            priority: destination.index,
          }

          if (source.droppableId === destination.droppableId) {
            payload['startIndex'] = source.index
          }

          dispatch(addImage(payload))
        }}
      >
        <Col span={8} className='gallery'>
          <Row>
            <Col className='text-title'>{renderTranslation(language, TranslationKeys.imagesSection)}</Col>
            <Col>
              <SaveFilled className={'generic-icon save-icon ' + (isEdited ? ' active' : '')} onClick={saveChanges} />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              {galleries.map((gallery: Gallery) => {
                const imageList = gallery.images || []
                return (
                  <Droppable
                    key={`${gallery.id}`}
                    droppableId={`gallery-${gallery.id}`}
                    direction='horizontal'
                    renderClone={(provided, snapshot, rubric) =>
                      setDraggableImageComponent(
                        'drag-wrapper cursor-grab pa-2' + (snapshot.isDragging ? ' on-drag' : ''),
                        'image-preview',
                        provided,
                        snapshot,
                        retrieveImageIdFromDraggableId(rubric.draggableId),
                        images.find(x => x.id === parseInt(retrieveImageIdFromDraggableId(rubric.draggableId)))?.url,
                        false,
                      )
                    }
                    getContainerForClone={() => {
                      return imageSection
                    }}
                  >
                    {(droppableProvided, droppableSnapshot) => (
                      <PimGallery
                        imageList={imageList}
                        gallery={gallery}
                        ref={droppableProvided.innerRef}
                        droppableSnapshot={droppableSnapshot}
                        droppableProvided={droppableProvided}
                      />
                    )}
                  </Droppable>
                )
              })}
            </Col>
          </Row>
        </Col>
        <Col span={14} offset={2} className='photo-box'>
          <Row>
            <Col flex='auto' className='text-title pb-2'>
              {renderTranslation(language, TranslationKeys.photoBox)}
            </Col>
            {/*
            <Col flex='30px'>
              <UploadImageButton />
            </Col>
             */}
          </Row>
          <Droppable
            droppableId={photoboxDroppableId}
            isDropDisabled={true}
            direction='horizontal'
            renderClone={(provided, snapshot, rubric) =>
              setDraggableImageComponent(
                'photo drag-wrapper cursor-grab on-drag clone',
                '',
                provided,
                snapshot,
                rubric.draggableId,
                images.find(x => x.id === parseInt(rubric.draggableId))?.url,
                false,
              )
            }
            getContainerForClone={() => {
              return imageSection
            }}
          >
            {(droppableProvided, droppableSnapshot) => (
              <Row
                className='photo-container flex-wrap'
                gutter={[16, 16]}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {images.map((image: Image, index: number) => {
                  const usedCount = galleries
                    .map(x => {
                      return x.images
                    })
                    .reduce((pre, cur) => {
                      return pre.concat(cur)
                    })
                    .filter(x => x.id === image.id).length

                  return (
                    <Col key={`img-container-${image.id}`} className='ma-2'>
                      <Draggable draggableId={`${image.id}`} index={index}>
                        {(draggableProvided, draggableSnapshot) =>
                          setDraggableImageComponent(
                            'photo drag-wrapper cursor-grab',
                            '',
                            draggableProvided,
                            draggableSnapshot,
                            image.id,
                            image.url,
                            false,
                            usedCount,
                            setEditId,
                            image.type,
                          )
                        }
                      </Draggable>
                    </Col>
                  )
                })}
                {droppableProvided.placeholder}
              </Row>
            )}
          </Droppable>
        </Col>
      </DragDropContext>
    </Row>
  ) : null
}

export default Images
